<template>
  <div>
    <Head :title="$t('message.leave.edit_leave')" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/leave/leaves">{{ $t('message.leave.leave') }}</Link>
      <span class="text-indigo-400 font-medium">/</span>
      {{ $t('message.leave.requesting_staff') }} <span class="text-indigo-400">{{ leave.requesting_staff.name }}</span>
    </h1>
    <trashed-message v-if="leave.deleted_at" class="mb-6" @restore="restore"> This leave has been deleted. </trashed-message>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <select-input v-if="hasRole(['admin', 'exec', 'hr', 'department head']) && this.leave.status != 'finalised'" :disabled="this.leave.status == 'finalised'" v-model="form.status" :error="form.errors.status" class="lg: pb-8 pr-6 w-full" label="Status">
            <option :value="null" />
            <option v-for="stat in status" :key="stat.id" :value="stat.id">
              {{ $t(`message.leave.${slugifyStatus(stat.name)}`) }}
            </option>
          </select-input>
          <select-input v-if="hasRole(['admin', 'exec', 'hr', 'department head'])" :disabled="this.leave.status == 'finalised'" v-model="form.relief_staff_id" :error="form.errors.relief_staff_id" class="lg: pb-8 pr-6 w-full" label="Relief Staff">
            <option :value="null" />
            <option v-for="staff in staffs" :key="staff.id" :value="staff.id">{{ staff.name }}</option>
          </select-input>
          <date-picker :disabled="this.leave.status == 'finalised'" v-model="form.start_date" :error="form.errors.start_date" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.start_date')" />
          <date-picker :disabled="this.leave.status == 'finalised'" v-model="form.end_date" :error="form.errors.end_date" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.end_date')" />
          <text-input :disabled="this.leave.status == 'finalised'" v-model="form.num_of_leave_days" type="number" :error="form.errors.end_date" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.num_of_days')" />
          <select-input :disabled="this.leave.status == 'finalised'" v-model="form.leave_type_id" :error="form.errors.leave_type_id" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.leave_type')">
            <option :value="null" />
            <option v-for="leave_type in leave_types" :key="leave_type.id" :value="leave_type.id">{{ leave_type.title }}</option>
          </select-input>
          <text-input :disabled="this.leave.status == 'finalised'" v-model="form.leave_address" :error="form.errors.leave_address" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.leave_address')" />
          <text-input :disabled="this.leave.status == 'finalised'" v-model="form.leave_phone" :error="form.errors.leave_phone" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.leave_phone')" />
          <text-input :disabled="this.leave.status == 'finalised'" v-model="form.leave_email" :error="form.errors.leave_email" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.leave_email')" />
          <div v-if="dateOfResume != ''">
            <p class="pb-8 pr-6 text-gray-600 text-lg">
              {{ $t('message.leave.leave_date_of_resuming') }}
              <span class="font-semibold">{{ formatDate(dateOfResume) }}</span>
            </p>
            <p class="pb-8 pr-6 text-gray-600 text-lg">
              {{ $t('message.leave.leave_end_date') }}
              <span class="font-semibold">{{ formatDate(endDate) }}</span>
            </p>
          </div>
          <p v-if="this.form.leave_type_id != 4" class="pb-8 pr-6 w-full text-gray-400 text-lg font-semibold">{{ $t('message.leave.leave_outsanding_days') }} {{ form.remaining_days }}</p>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button v-if="!leave.deleted_at && leave.status != 'finalised'" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">
            {{ $t('message.leave.delete_leave') }}
          </button>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
            {{ $t('message.leave.update_leave') }}
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput.vue'
import DatePicker from '@/Shared/DatePicker'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import RoleChecker from '../../../Shared/Helpers/RoleChecker'

export default {
  components: {
    Head,
    Icon,
    Link,
    LoadingButton,
    TextInput,
    TextareaInput,
    DatePicker,
    SelectInput,
    TrashedMessage,
    RoleChecker,
  },
  layout: Layout,
  props: {
    leave: Object,
    leave_types: Array,
    status: Array,
    staffs: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        requesting_staff_id: this.leave.requesting_staff_id,
        leave_type_id: this.leave.leave_type_id,
        start_date: this.leave.start_date,
        end_date: this.leave.end_date,
        resume_date: this.leave.resume_date,
        num_of_leave_days: this.leave.num_of_leave_days,
        leave_address: this.leave.leave_address,
        leave_phone: this.leave.leave_phone,
        leave_email: this.leave.leave_email,
        relief_staff_id: this.leave.relief_staff_id,
        remaining_days: this.leave.remaining_days,
        status: this.leave.status,
      }),
      dateOfResume: '',
      endDate: '',
    }
  },
  watch: {
    'form.start_date': function (val) {
      if (this.form.num_of_leave_days) {
        const { resumeDate, endDate } = this.calculateDates(this.form.num_of_leave_days)
        this.dateOfResume = resumeDate
        this.endDate = endDate
        this.form.end_date = endDate
        this.form.resume_date = resumeDate
      }
    },
    'form.num_of_leave_days': function (val) {
      if (this.form.start_date) {
        const { resumeDate, endDate } = this.calculateDates(this.form.num_of_leave_days)
        this.dateOfResume = resumeDate
        this.endDate = endDate
        this.form.end_date = endDate
        this.form.resume_date = resumeDate
      }
    },
  },
  mounted() {
    const { resumeDate, endDate } = this.calculateDates(this.form.num_of_leave_days)
    this.dateOfResume = resumeDate
    this.endDate = endDate
    this.form.resume_date = resumeDate
  },

  methods: {
    hasRole(role) {
      var userRole = new RoleChecker(this.$page.props)
      return userRole.hasRole(role)
    },

    calculateDates(numOfDays) {
      const startDateObj = new Date(this.form.start_date)

      // First adjust start date if it falls on weekend
      this.adjustForWeekend(startDateObj)

      // Count the leave days
      let remainingDays = parseInt(numOfDays)
      const endDate = new Date(startDateObj)

      // Count the actual leave days
      while (remainingDays > 0) {
        if (this.isBusinessDay(endDate)) {
          remainingDays--
        }
        if (remainingDays > 0) {
          endDate.setDate(endDate.getDate() + 1)
        }
      }

      // Calculate resume date (next business day after end date)
      const resumeDate = new Date(endDate)
      resumeDate.setDate(resumeDate.getDate() + 1)
      this.adjustForWeekend(resumeDate)

      // Make sure end date is a business day
      this.adjustForWeekendBackward(endDate)

      return {
        resumeDate: resumeDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      }
    },

    adjustForWeekend(date) {
      const day = date.getDay()
      if (day === 0) {
        // Sunday
        date.setDate(date.getDate() + 1)
      } else if (day === 6) {
        // Saturday
        date.setDate(date.getDate() + 2)
      }
    },

    adjustForWeekendBackward(date) {
      const day = date.getDay()
      if (day === 0) {
        // Sunday
        date.setDate(date.getDate() - 2) // Move to Friday
      } else if (day === 6) {
        // Saturday
        date.setDate(date.getDate() - 1) // Move to Friday
      }
    },

    isBusinessDay(date) {
      if (!(date instanceof Date)) {
        date = new Date(date)
      }
      const day = date.getDay()
      return day !== 0 && day !== 6 // Not Sunday (0) and not Saturday (6)
    },

    formatDate(date) {
      const locale = this.$t('message.locale')
      var date = new Date(date).toLocaleDateString(locale, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      date = date.split(' ')[0] + ' ' + date.split(' ')[1] + ' ' + date.split(' ')[2].charAt(0).toUpperCase() + date.split(' ')[2].slice(1) + ' ' + date.split(' ')[3]
      return date.charAt(0).toUpperCase() + date.slice(1)
    },
    update() {
      this.form.put(`/leave/leaves/${this.leave.id}`)
    },
    destroy() {
      if (confirm('Are you sure you want to delete this leave?')) {
        this.$inertia.delete(`/leave/leaves/${this.leave.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this leave?')) {
        this.$inertia.put(`/leave/leaves/${this.leave.id}/restore`)
      }
    },
    slugifyStatus(status) {
      return status.toLowerCase().replace(' ', '-')
    },
  },
}
</script>
